import { graphemeLength, sliceGraphemes } from '@up2stay/up2promo-commons';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { useState } from 'react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  useEntryGovernance,
  WholeModuleGovernanceHeader,
} from '@/core/components/profile/module-entry-governance-ui';
import { TextareaWithCharCount } from '@/core/components/ui/field-with-char-count';
import { MODULE_ENTRY_WHOLE_MODULE_KEY } from '@/core/lib/profile-module-entry-policies';
import { extractYouTubeVideoId } from '@/core/lib/youtube';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type { VideoModuleConfig } from '@/core/types/profile-modules';

const VIDEO_DESCRIPTION_MAX_LENGTH = 200;

interface VideoModuleConfigProps extends ModuleEntryGovernanceProps {
  config: VideoModuleConfig;
  onChange: (config: VideoModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

export function VideoModuleConfigComponent({
  config,
  onChange,
  contentReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: VideoModuleConfigProps) {
  const dict = useDictionary();
  const videoDict = dict.profiles.edit.modules.video;
  const [localConfig, setLocalConfig] = useState<VideoModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const { entryLocked } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });

  const handleChange = (updates: Partial<VideoModuleConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const description = localConfig.description ?? '';
  const setDescription = (value: string) => {
    handleChange({
      description:
        graphemeLength(value) <= VIDEO_DESCRIPTION_MAX_LENGTH
          ? value
          : sliceGraphemes(value, VIDEO_DESCRIPTION_MAX_LENGTH),
    });
  };

  const inputsDisabled = entryLocked && !showEntryPolicies;
  const urlValue = localConfig.url || '';
  const parsedVideoId = extractYouTubeVideoId(urlValue);
  const urlInvalid = urlValue.trim().length > 0 && parsedVideoId == null;

  return (
    <div className="space-y-4">
      <WholeModuleGovernanceHeader
        templateModule={templateModule}
        showEntryPolicy={showEntryPolicies}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        fieldLockedHintMessage={fieldLockedHintMessage}
        entryLabel="Video"
        memberDialogTitle="YouTube Video"
        memberValueColumn={{
          kind: 'moduleEntry',
          entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
          moduleId: templateModule?.id,
          label: 'Video',
        }}
        memberDefaultValue={localConfig.url || ''}
        workspaceId={workspaceId}
        templateId={templateId}
        publishStatus={publishStatus}
        memberRoster={memberRoster}
        memberRosterLoading={memberRosterLoading}
        hasAssignedTeams={hasAssignedTeams}
        memberValuesDict={memberValuesDict}
        canManage={!contentReadOnly}
      />
      <div className="space-y-2">
        <Label htmlFor="video-url">{videoDict.urlLabel}</Label>
        <Input
          id="video-url"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={urlValue}
          onChange={(e) => {
            const raw = e.target.value;
            const id = extractYouTubeVideoId(raw);
            handleChange({ url: id != null ? id : raw });
          }}
          placeholder={videoDict.urlPlaceholder}
          className="w-full"
          disabled={inputsDisabled}
          aria-invalid={urlInvalid}
        />
        {urlInvalid ? (
          <p className="text-sm text-destructive">{videoDict.urlInvalid}</p>
        ) : null}
        {urlInvalid ? (
          <p className="text-sm text-muted-foreground">{videoDict.notShown}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="video-description">Beschreibung (optional)</Label>
        <TextareaWithCharCount
          id="video-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Kurze Beschreibung"
          rows={2}
          maxLength={VIDEO_DESCRIPTION_MAX_LENGTH}
          valueLength={graphemeLength(description)}
          className="w-full"
          disabled={inputsDisabled}
        />
      </div>
    </div>
  );
}
